import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Award, TrendingUp, RotateCcw } from 'lucide-react';
import { learningAPI } from '../../services/api';

const QuizResults = () => {
    const { quizId } = useParams();
    const navigate = useNavigate();
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchQuizResults();
    }, [quizId]);

    const fetchQuizResults = async () => {
        try {
            const response = await learningAPI.getQuizResults(quizId);
            setResults(response.data.results || getSampleResults());
        } catch (err) {
            console.error('Error fetching quiz results:', err);
            setError('Unable to load quiz results');
            setResults(getSampleResults());
        } finally {
            setLoading(false);
        }
    };

    const getSampleResults = () => ({
            quiz_id: quizId || 'quiz_123',
            quiz_title: 'English Grammar Fundamentals',
            course_name: 'Grammar Excellence',
            completed_at: '2026-02-15T14:30:00Z',
            duration: 1245, // seconds
            score: 85,
            total_questions: 20,
            correct_answers: 17,
            incorrect_answers: 3,
            passed: true,
            pass_threshold: 70,
            questions: [
                {
                    id: 1,
                    question: 'Which sentence is grammatically correct?',
                    user_answer: 'She has been working here for five years.',
                    correct_answer: 'She has been working here for five years.',
                    is_correct: true,
                    options: [
                        'She has been working here for five years.',
                        'She is working here for five years.',
                        'She working here for five years.',
                        'She works here for five years.'
                    ],
                    explanation: 'Present perfect continuous is used for actions that started in the past and continue to the present.'
                },
                {
                    id: 2,
                    question: 'Choose the correct form: "If I _____ you, I would apologize."',
                    user_answer: 'am',
                    correct_answer: 'were',
                    is_correct: false,
                    options: ['am', 'was', 'were', 'be'],
                    explanation: 'In second conditional sentences, we use "were" for all subjects, not "was".'
                },
                {
                    id: 3,
                    question: 'Identify the passive voice sentence:',
                    user_answer: 'The book was written by Shakespeare.',
                    correct_answer: 'The book was written by Shakespeare.',
                    is_correct: true,
                    options: [
                        'Shakespeare wrote the book.',
                        'The book was written by Shakespeare.',
                        'Shakespeare is writing a book.',
                        'The book writes Shakespeare.'
                    ],
                    explanation: 'Passive voice uses the structure: subject + be verb + past participle + by + agent'
                }
                // Add more mock questions as needed
            ],
            performance_by_topic: [
                { topic: 'Tenses', correct: 5, total: 6, percentage: 83 },
                { topic: 'Articles', correct: 4, total: 4, percentage: 100 },
                { topic: 'Conditionals', correct: 3, total: 5, percentage: 60 },
                { topic: 'Passive Voice', correct: 5, total: 5, percentage: 100 }
            ]
        });

    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
    };

    const getScoreColor = (score) => {
        if (score >= 80) return '#27B05B';
        if (score >= 60) return '#FF9800';
        return '#E74C3C';
    };

    const handleRetakeQuiz = () => {
        navigate(`/quiz/${quizId}`);
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loading}>
                    <div style={styles.spinner}></div>
                    <p>Loading quiz results...</p>
                </div>
            </div>
        );
    }

    if (!results) {
        return (
            <div style={styles.container}>
                <div style={styles.error}>
                    <p>Quiz results not found</p>
                    <button onClick={() => navigate('/practice')} style={styles.backButton}>
                        Back to Practice
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.resultHeader}>
                <div style={styles.resultCard}>
                    <div style={results.passed ? styles.passedIcon : styles.failedIcon}>
                        {results.passed ? <Award size={48} /> : <XCircle size={48} />}
                    </div>
                    <h1 style={styles.resultTitle}>
                        {results.passed ? 'Congratulations!' : 'Keep Practicing!'}
                    </h1>
                    <p style={styles.resultSubtitle}>{results.quiz_title}</p>
                    
                    <div style={styles.scoreDisplay}>
                        <div style={{...styles.scoreCircle, borderColor: getScoreColor(results.score)}}>
                            <span style={{...styles.scoreValue, color: getScoreColor(results.score)}}>
                                {results.score}%
                            </span>
                        </div>
                    </div>

                    <div style={styles.quickStats}>
                        <div style={styles.quickStat}>
                            <CheckCircle size={20} color="#27B05B" />
                            <span>{results.correct_answers} Correct</span>
                        </div>
                        <div style={styles.quickStat}>
                            <XCircle size={20} color="#E74C3C" />
                            <span>{results.incorrect_answers} Incorrect</span>
                        </div>
                        <div style={styles.quickStat}>
                            <Clock size={20} color="#3498DB" />
                            <span>{formatDuration(results.duration)}</span>
                        </div>
                    </div>

                    <button onClick={handleRetakeQuiz} style={styles.retakeButton}>
                        <RotateCcw size={20} />
                        Retake Quiz
                    </button>
                </div>
            </div>

            {results.performance_by_topic && (
                <div style={styles.section}>
                    <h2 style={styles.sectionTitle}>
                        <TrendingUp size={24} />
                        Performance by Topic
                    </h2>
                    <div style={styles.topicsGrid}>
                        {results.performance_by_topic.map(topic => (
                            <div key={topic.topic} style={styles.topicCard}>
                                <div style={styles.topicHeader}>
                                    <h3 style={styles.topicName}>{topic.topic}</h3>
                                    <span style={{
                                        ...styles.topicScore,
                                        color: getScoreColor(topic.percentage)
                                    }}>
                                        {topic.percentage}%
                                    </span>
                                </div>
                                <div style={styles.topicStats}>
                                    {topic.correct} / {topic.total} correct
                                </div>
                                <div style={styles.progressBarContainer}>
                                    <div 
                                        style={{
                                            ...styles.progressBar,
                                            width: `${topic.percentage}%`,
                                            backgroundColor: getScoreColor(topic.percentage)
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>Detailed Breakdown</h2>
                
                <div style={styles.questionsList}>
                    {results.questions.map((q, index) => (
                        <div key={q.id} style={styles.questionCard}>
                            <div style={styles.questionHeader}>
                                <div style={styles.questionNumber}>
                                    Question {index + 1}
                                </div>
                                {q.is_correct ? (
                                    <div style={styles.correctBadge}>
                                        <CheckCircle size={18} />
                                        Correct
                                    </div>
                                ) : (
                                    <div style={styles.incorrectBadge}>
                                        <XCircle size={18} />
                                        Incorrect
                                    </div>
                                )}
                            </div>

                            <h3 style={styles.questionText}>{q.question}</h3>

                            <div style={styles.answersSection}>
                                {q.options.map((option, idx) => {
                                    const isUserAnswer = option === q.user_answer;
                                    const isCorrectAnswer = option === q.correct_answer;
                                    
                                    let optionStyle = styles.optionItem;
                                    if (isCorrectAnswer) {
                                        optionStyle = {...optionStyle, ...styles.optionCorrect};
                                    } else if (isUserAnswer && !q.is_correct) {
                                        optionStyle = {...optionStyle, ...styles.optionIncorrect};
                                    }

                                    return (
                                        <div key={idx} style={optionStyle}>
                                            {isCorrectAnswer && <CheckCircle size={18} color="#27B05B" />}
                                            {isUserAnswer && !q.is_correct && <XCircle size={18} color="#E74C3C" />}
                                            <span>{option}</span>
                                            {isUserAnswer && (
                                                <span style={styles.yourAnswerTag}>Your Answer</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {q.explanation && (
                                <div style={styles.explanationBox}>
                                    <strong style={styles.explanationTitle}>Explanation:</strong>
                                    <p style={styles.explanationText}>{q.explanation}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '900px',
        margin: '0 auto',
        padding: '20px',
        minHeight: '100vh'
    },
    resultHeader: {
        marginBottom: '40px'
    },
    resultCard: {
        backgroundColor: 'white',
        borderRadius: '20px',
        padding: '40px',
        textAlign: 'center',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
    },
    passedIcon: {
        width: '80px',
        height: '80px',
        margin: '0 auto 20px',
        borderRadius: '50%',
        backgroundColor: '#E8F5E9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#27B05B'
    },
    failedIcon: {
        width: '80px',
        height: '80px',
        margin: '0 auto 20px',
        borderRadius: '50%',
        backgroundColor: '#FFEBEE',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#E74C3C'
    },
    resultTitle: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#2C3E50',
        marginBottom: '10px'
    },
    resultSubtitle: {
        fontSize: '18px',
        color: '#666',
        marginBottom: '30px'
    },
    scoreDisplay: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '30px'
    },
    scoreCircle: {
        width: '160px',
        height: '160px',
        borderRadius: '50%',
        border: '10px solid',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    },
    scoreValue: {
        fontSize: '48px',
        fontWeight: '700'
    },
    quickStats: {
        display: 'flex',
        justifyContent: 'center',
        gap: '30px',
        marginBottom: '30px',
        flexWrap: 'wrap'
    },
    quickStat: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '16px',
        fontWeight: '600',
        color: '#2C3E50'
    },
    retakeButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        padding: '14px 40px',
        backgroundColor: '#3498DB',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '16px',
        fontWeight: '700',
        cursor: 'pointer',
        margin: '0 auto'
    },
    section: {
        marginBottom: '40px'
    },
    sectionTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '24px',
        fontWeight: '700',
        color: '#2C3E50',
        marginBottom: '20px'
    },
    topicsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '15px'
    },
    topicCard: {
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    topicHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px'
    },
    topicName: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#2C3E50'
    },
    topicScore: {
        fontSize: '18px',
        fontWeight: '700'
    },
    topicStats: {
        fontSize: '13px',
        color: '#666',
        marginBottom: '10px'
    },
    progressBarContainer: {
        width: '100%',
        height: '8px',
        backgroundColor: '#E0E0E0',
        borderRadius: '4px',
        overflow: 'hidden'
    },
    progressBar: {
        height: '100%',
        borderRadius: '4px',
        transition: 'width 0.5s ease'
    },
    questionsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '25px'
    },
    questionCard: {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '25px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    questionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px'
    },
    questionNumber: {
        fontSize: '14px',
        fontWeight: '700',
        color: '#3498DB',
        textTransform: 'uppercase'
    },
    correctBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        backgroundColor: '#E8F5E9',
        color: '#27B05B',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: '600'
    },
    incorrectBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        backgroundColor: '#FFEBEE',
        color: '#E74C3C',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: '600'
    },
    questionText: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#2C3E50',
        marginBottom: '20px',
        lineHeight: '1.6'
    },
    answersSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '20px'
    },
    optionItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '15px',
        backgroundColor: '#F9F9F9',
        borderRadius: '8px',
        fontSize: '15px',
        color: '#2C3E50',
        border: '2px solid transparent'
    },
    optionCorrect: {
        backgroundColor: '#E8F5E9',
        borderColor: '#27B05B',
        fontWeight: '600'
    },
    optionIncorrect: {
        backgroundColor: '#FFEBEE',
        borderColor: '#E74C3C'
    },
    yourAnswerTag: {
        marginLeft: 'auto',
        fontSize: '12px',
        fontWeight: '600',
        color: '#666',
        fontStyle: 'italic'
    },
    explanationBox: {
        padding: '15px',
        backgroundColor: '#F0F8FF',
        borderLeft: '4px solid #3498DB',
        borderRadius: '8px'
    },
    explanationTitle: {
        display: 'block',
        fontSize: '14px',
        color: '#3498DB',
        marginBottom: '8px'
    },
    explanationText: {
        fontSize: '14px',
        color: '#2C3E50',
        lineHeight: '1.6',
        margin: 0
    },
    loading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        gap: '20px'
    },
    spinner: {
        width: '50px',
        height: '50px',
        border: '5px solid #f3f3f3',
        borderTop: '5px solid #27B05B',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    error: {
        textAlign: 'center',
        padding: '50px',
        color: '#E74C3C'
    },
    backButton: {
        marginTop: '20px',
        padding: '12px 30px',
        backgroundColor: '#27B05B',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer'
    }
};

export default QuizResults;
